import { Check, CheckCheck, CircleCheck, History, Settings, Undo2, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb, type BreadcrumbSegment } from '../../../components/Breadcrumb';
import { Button } from '../../../components/Button';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { ErrorState } from '../../../components/ErrorState';
import { Menu } from '../../../components/Menu';
import { CountBadge } from '../../../components/CountBadge';
import { PreviewGrid } from '../../../components/PreviewGrid';
import { PreviewTable } from '../../../components/PreviewTable';
import { Skeleton } from '../../../components/Skeleton';
import { HistoryLogsModal } from '../../../components/index-page/HistoryLogsModal';
import { PermissionGate } from '../../../auth/PermissionGate';
import { useAuth } from '../../../auth/AuthContext';
import { ProfileBanner } from '../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../components/profile/ProfileHeaderShell';
import { StatusText } from '../../../components/table/StatusText';
import { useToast } from '../../../components/toastContext';
import { TextCell } from '../../../components/table/TextCell';
import { ApiError } from '../../../lib/apiError';
import { formatDateTime } from '../../../lib/formatDate';
import { RejectApprovalModal } from '../components/RejectApprovalModal';
import { SubjectDetailCard } from '../components/SubjectDetailCard';
import { approvalsApi } from '../approvalsApi';
import {
  APPROVAL_STATUS,
  APPROVAL_STEP_STATUS,
  DECISION_FAILED,
  approvalStatusTone,
  decisionMessage,
} from '../approvalTypes';
import type { ApprovalRequestDetail, ApprovalStep } from '../approvalTypes';

type TabKey = 'info' | 'chain';

/**
 * The decision a reader has pressed and not yet confirmed.
 *
 * ONE piece of state rather than a boolean each, because the three are mutually exclusive —
 * three flags would make every impossible combination expressible and leave the page deciding
 * which dialog wins.
 */
type PendingDecision = 'approve' | 'acknowledge' | 'retract' | null;

/**
 * One approval request — what was submitted, who signs it, and how it was decided.
 *
 * On the **header** layout: a request reads as one continuous thing rather than a record whose
 * content splits into panels a reader picks between.
 *
 * **IT DOES SHOW WHAT THE SUBJECT SAYS, and that reverses what this page shipped with.** The
 * comment here read "the engine holds a reference and a one-line summary and nothing else";
 * product call, 29 Sep 2026, because an approver cannot sign a purchase request off one line.
 * The decoupling is untouched — the MODULE composes the detail through its handler, the shape
 * names no module, and the API strips it from a caller who does not hold that module's own
 * read key. See `<SubjectDetailCard>` and CLAUDE.md, "The profile SHOWS WHAT IS BEING APPROVED".
 *
 * **EVERY DECISION ASKS BEFORE IT WRITES.** Approving, acknowledging and retracting each open a
 * `<ConfirmDialog requireConfirm>`; rejecting opens a form instead, because it has to COLLECT
 * the reason. Approving used to commit straight from the banner, which put an irreversible
 * decision on somebody else's record one stray click away.
 */
export function ApprovalProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const toast = useToast();

  const [request, setRequest] = useState<ApprovalRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [acting, setActing] = useState(false);
  const [pending, setPending] = useState<PendingDecision>(null);
  const [rejecting, setRejecting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('info');

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!id) return;

      setLoading(true);

      approvalsApi
        .get(id, signal)
        .then((next) => {
          if (!signal?.aborted) setRequest(next);
        })
        .catch((caught: unknown) => {
          if (signal?.aborted) return;

          setError(
            caught instanceof ApiError
              ? caught
              : new ApiError('Could not load this approval request.', 0),
          );
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);

    return () => controller.abort();
  }, [load]);

  /**
   * Records a decision, and says what became of it.
   *
   * **BOTH OUTCOMES GO TO THE TOAST, because the dialog that asked is gone by the time either
   * is known.** The `finally` closes it whether the write landed or not, so a success had
   * nothing on screen to show for it at all — the banner changed and the buttons vanished,
   * which is a reader INFERRING the result rather than being told it.
   *
   * **It deliberately does NOT touch `error`.** That state belongs to the LOAD, and its strip
   * says *what is on screen may be stale*; a decision's refusal is a different event, and
   * putting it there made one strip answer two questions. A refused decision leaves the record
   * exactly as it was, which is what the toast then says.
   */
  async function decide(verb: string, action: () => Promise<ApprovalRequestDetail>) {
    /* Read BEFORE the write, because `setRequest` replaces the record the name comes from. */
    const subject = request?.subjectReference ?? null;

    setActing(true);

    try {
      setRequest(await action());
      toast.success(decisionMessage(verb, subject));
    } catch (caught) {
      toast.failure(caught instanceof ApiError ? caught.message : DECISION_FAILED);
    } finally {
      setActing(false);
      setPending(null);
    }
  }

  const breadcrumb: BreadcrumbSegment[] = [
    'Approvals',
    { label: 'Requests', to: '/approvals/requests' },
    request?.subjectReference ?? 'Request',
  ];

  /*
    A FAILED REFRESH MUST NOT REPLACE THE PAGE. The full-page error state is for a failure with
    nothing to show; where a record is already up it is still the last good answer, so the
    guard is on `error && !request` rather than on `error` alone.
  */
  if (error && !request) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb segments={breadcrumb} />
        <ErrorState message={error.message} onRetry={() => load()} />
      </div>
    );
  }

  if (loading && !request) {
    return (
      <div className="flex h-full flex-col">
        <Breadcrumb segments={breadcrumb} />
        <div className="flex flex-col gap-5 p-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full max-w-4xl" />
        </div>
      </div>
    );
  }

  if (!request) return null;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-slate-100">
      {/*
        THE BREADCRUMB IS THE PAGE'S, not the shell's — and it IS the back-nav, so every segment
        above the record links and there is no Back button anywhere on here.
      */}
      <Breadcrumb segments={breadcrumb} />

      <ProfileHeaderShell
        id="approval-profile"
        header={
          <ProfileHeader
            /*
              THE ICON DISC, like every other profile in the product — product call,
              29 Sep 2026, and it REVERSES the submitter's avatar this header shipped with.

              That argument was that an approval request is a thing one person asked another
              to sign, so the leading mark should be theirs. What it cost is that this was the
              ONLY profile of thirty-seven drawing a face where every other one draws its
              module's glyph, and an initials disc reads as *a person's record* — which is what
              `<Avatar>` means on the users and personnel profiles one rail along.

              The submitter is not lost: they are named on the `secondary` line, which is where
              a fact about the record belongs rather than in the slot that says what KIND of
              record this is.
            */
            icon={<CheckCheck className="h-6 w-6" aria-hidden="true" />}
            title={request.subjectReference ?? 'Approval request'}
            meta={[request.workflowName, request.modeLabel]}
            secondary={[
              request.subjectSummary,
              request.requestedByName ? `Submitted by ${request.requestedByName}` : null,
            ].filter((v): v is string => Boolean(v))}
            status={
              <StatusText tone={approvalStatusTone(request.status)}>
                {request.statusLabel}
              </StatusText>
            }
            createdBy={request.createdByName}
            createdAt={request.createdAt}
            actions={
              <Menu
                id="approval-profile-cog"
                items={[
                  {
                    key: 'history',
                    label: 'History Logs',
                    icon: <History className="h-4 w-4" aria-hidden="true" />,
                    permission: 'audit-logs.read',
                    onClick: () => setHistoryOpen(true),
                  },
                ]}
                trigger={({ open, toggle }) => (
                  <button
                    type="button"
                    onClick={toggle}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label="Request options"
                    className="rounded-lg p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-200"
                  >
                    <Settings className="h-4 w-4" aria-hidden="true" />
                  </button>
                )}
              />
            }
          />
        }
        banner={
          /*
            THE RECORD-LEVEL LIFECYCLE ACTIONS LIVE HERE, which is where an action about the
            whole record belongs — the banner is on screen for every tab. It renders for every
            state, not only the actionable ones: a banner that appears only when something is
            waiting trains the reader to skip the strip.
          */
          <ProfileBanner
            tone={request.status === APPROVAL_STATUS.pending ? 'blue' : 'slate'}
            message={bannerMessage(request)}
            action={
              request.status === APPROVAL_STATUS.pending ? (
                <div className="flex items-center gap-2">
                  {/*
                    EVERY ICON GOES THROUGH `leading`, NEVER AS A CHILD. `<Button>` wraps its
                    children in a `<span>`, and Tailwind's preflight sets `svg { display:
                    block }` — so an icon passed as a child is a block box inside inline
                    content and pushes the label onto a second line. It shipped exactly that
                    way here, which is CLAUDE.md's own worked example arriving on the screen
                    it was written about.

                    AND EVERY ONE OF THEM ASKS BEFORE IT WRITES. A decision on somebody else's
                    record is not something a stray click should commit.
                  */}
                  {request.canApprove ? (
                    <PermissionGate permission="approvals.decide">
                      <Button
                        id="approval-approve"
                        variant="primary"
                        leading={<Check className="h-4 w-4" aria-hidden="true" />}
                        onClick={() => setPending('approve')}
                        disabled={acting}
                      >
                        Approve
                      </Button>
                    </PermissionGate>
                  ) : null}

                  {request.canAcknowledge ? (
                    <PermissionGate permission="approvals.decide">
                      <Button
                        id="approval-acknowledge"
                        variant="primary"
                        leading={<CircleCheck className="h-4 w-4" aria-hidden="true" />}
                        onClick={() => setPending('acknowledge')}
                        disabled={acting}
                      >
                        Acknowledge
                      </Button>
                    </PermissionGate>
                  ) : null}

                  {request.canApprove ? (
                    <PermissionGate permission="approvals.decide">
                      <Button
                        id="approval-reject"
                        variant="secondary"
                        leading={<X className="h-4 w-4" aria-hidden="true" />}
                        onClick={() => setRejecting(true)}
                        disabled={acting}
                      >
                        Reject
                      </Button>
                    </PermissionGate>
                  ) : null}

                  {request.canRetract ? (
                    <PermissionGate permission="approvals.read">
                      <Button
                        id="approval-retract"
                        variant="secondary"
                        leading={<Undo2 className="h-4 w-4" aria-hidden="true" />}
                        onClick={() => setPending('retract')}
                        disabled={acting}
                      >
                        Retract
                      </Button>
                    </PermissionGate>
                  ) : null}
                </div>
              ) : undefined
            }
            id="approval-profile-banner"
          />
        }
        tabs={[
          { key: 'info', label: 'Info' },
          {
            key: 'chain',
            /*
              THE COUNT GOES INSIDE THE LABEL via `<CountBadge>` — there is no separate count
              prop, and a zero renders, which is what tells a reader the tab is empty before
              they click it.
            */
            label: (
              <>
                Chain <CountBadge count={request.steps.length} />
              </>
            ),
          },
        ]}
        activeTab={tab}
        onTabChange={(next) => setTab(next as TabKey)}
      >
        {/*
          THE LOAD'S OWN STRIP, and nothing else reaches it. A failed REFRESH must not replace
          the page — what is up is still the last good answer — so it says the record may be
          stale rather than printing a bare message a reader cannot place.

          A DECISION's refusal does not come here. It goes to the toast, because by then the
          dialog that asked is closed and the record on screen is unchanged, which is a
          different thing from the page being out of date.
        */}
        {error ? (
          <div role="alert" className="max-w-4xl rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">
              This request could not be refreshed, so what is shown may be out of date.{' '}
              {error.message}
            </p>
          </div>
        ) : null}

        {tab === 'info' ? (
          <>
            {/*
              WHAT IS BEING APPROVED COMES FIRST. An approver opens this page to decide, and
              the thing they are deciding on is the subject — the engine's own facts below
              explain how the decision is taken rather than what it is about.
            */}
            <SubjectDetailCard
              request={request}
              canReadSubject={
                request.subjectReadPermission != null &&
                hasPermission(request.subjectReadPermission)
              }
            />

            <ProfileCard id="approval-profile-request" title="Request">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Reference', value: request.subjectReference, mono: true },
                  { label: 'Workflow', value: request.workflowName },
                  { label: 'Subject', value: request.subjectSummary },
                  { label: 'Decision', value: request.statusLabel },
                  { label: 'Routing', value: request.modeLabel },
                  {
                    /*
                      SURFACED rather than hidden, because it changes what the chain on the
                      other tab MEANS: five steps that all must sign and five of which any one
                      will do look identical otherwise.
                    */
                    label: 'Completion rule',
                    value: request.requireAll
                      ? 'Every approver must sign'
                      : 'Any one administrator completes it',
                  },
                  { label: 'Submitted by', value: request.requestedByName },
                  {
                    label: 'Decided',
                    value: request.completedAt ? formatDateTime(request.completedAt) : null,
                    mono: true,
                  },
                  { label: 'Outcome reason', value: request.outcomeReason },
                ]}
              />
            </ProfileCard>

            <ProfileCard id="approval-profile-audit" title="Audit">
              <PreviewTable
                variant="table-display"
                rows={[
                  { label: 'Created by', value: request.createdByName },
                  { label: 'Created', value: formatDateTime(request.createdAt), mono: true },
                  { label: 'Updated by', value: request.updatedByName },
                  {
                    label: 'Updated',
                    value: request.updatedAt ? formatDateTime(request.updatedAt) : null,
                    mono: true,
                  },
                ]}
              />
            </ProfileCard>
          </>
        ) : (
          <ProfileCard id="approval-profile-approvers" title="Approvers">
            {/*
              A PREVIEW GRID, not a panel: this collection is not WRITTEN from where it is
              drawn. A decision is taken on the whole request — the banner above — so search,
              sort, paging, selection and a row kebab would all be chrome over rows that are
              already on screen.
            */}
            <PreviewGrid<ApprovalStep>
              /*
                NO `#` COLUMN. The position would have to be derived from `sequenceOrder`, and
                a stored sort value is never a position — `order / 10 + 1` reads 1, 3, 4 the
                moment an approver is removed, and a meaningless number is worse than none.
                The rows arrive in chain order, which is what the reader actually needs.
              */
              columns={[
                { header: 'Approver', width: 220, render: (s) => <TextCell value={s.approverName ?? s.approverUserId} /> },
                { header: 'Signs', width: 140, render: (s) => <TextCell value={s.decisionTypeLabel} /> },
                { header: 'Activation', width: 140, render: (s) => <TextCell value={s.modeLabel} /> },
                { header: 'State', width: 160, render: (s) => <TextCell value={s.statusLabel} /> },
                {
                  header: 'Decided',
                  width: 170,
                  render: (s) => <TextCell value={s.decidedAt ? formatDateTime(s.decidedAt) : null} mono />,
                },
                { header: 'Reason', width: 260, render: (s) => <TextCell value={s.decisionReason} /> },
              ]}
              rows={request.steps}
              rowKey={(s) => s.id}
              empty="This request routed to no approvers."
            />
          </ProfileCard>
        )}
      </ProfileHeaderShell>

      {/*
        A DECISION IS CONFIRMED BEFORE IT IS WRITTEN, and all four of them are.

        These three are `<ConfirmDialog>` because the action is triggered OUTSIDE a modal —
        the banner — which is exactly what that component is for. Rejecting is the exception
        and is a form rather than a dialog, because it COLLECTS something: the reason, which
        is required and is the only thing the submitter is told. A `<ConfirmDialog>` cannot
        ask for a value, and stacking one over a form would break "one modal, never stacked".

        `requireConfirm` on each: a plain two-click dialog is two clicks in the same place,
        which a reader dismisses on reflex. No `requireCredential` — signing is an ordinary
        act of somebody's job, not a destruction, and password ceremony on a decision taken
        several times a day is ceremony people learn to click through.
      */}
      <ConfirmDialog
        open={pending === 'approve'}
        onClose={() => setPending(null)}
        onConfirm={() => decide('Approved', () => approvalsApi.approve(request.id))}
        title={request.subjectReference ? `Approve ${request.subjectReference}` : 'Approve request'}
        body={
          <p className="text-xs text-slate-600">
            {request.requireAll
              ? 'This signs your step. The request completes once every approver has signed, and the submitting module then advances its record.'
              : 'This signs the request. One signature completes it, and the submitting module advances its record straight away.'}
          </p>
        }
        loading={acting}
        requireConfirm
        id="approval-approve-confirm"
      />

      <ConfirmDialog
        open={pending === 'acknowledge'}
        onClose={() => setPending(null)}
        onConfirm={() => decide('Acknowledged', () => approvalsApi.acknowledge(request.id))}
        title={
          request.subjectReference
            ? `Acknowledge ${request.subjectReference}`
            : 'Acknowledge request'
        }
        body={
          <p className="text-xs text-slate-600">
            This records that you have seen the request. It is not an authorization, and there
            is no refusal to give — an acknowledge step cannot be rejected.
          </p>
        }
        loading={acting}
        requireConfirm
        id="approval-acknowledge-confirm"
      />

      <ConfirmDialog
        open={pending === 'retract'}
        onClose={() => setPending(null)}
        onConfirm={() => decide('Retracted', () => approvalsApi.retract(request.id))}
        title={request.subjectReference ? `Retract ${request.subjectReference}` : 'Retract request'}
        body={
          <p className="text-xs text-slate-600">
            This pulls your request back before it is decided. Nobody still waiting is asked to
            sign, the record returns to you to edit, and submitting it again starts a fresh
            chain.
          </p>
        }
        loading={acting}
        requireConfirm
        id="approval-retract-confirm"
      />

      {rejecting ? (
        <RejectApprovalModal
          open
          request={request}
          stepCount={request.steps.length}
          onClose={() => setRejecting(false)}
          onRejected={() => {
            setRejecting(false);
            load();
          }}
        />
      ) : null}

      {historyOpen ? (
        <HistoryLogsModal
          open
          onClose={() => setHistoryOpen(false)}
          module="approvals"
          /*
            SCOPED BY `entityId` ALONE, with no type bound. A record's own history matches on
            the parent id, and adding the type bound on top only subtracts — a write the server
            REFUSED never reached an entity, carries no entityType, and is exactly the event a
            reader opens a request's history to find.
          */
          entityId={request.id}
        />
      ) : null}
    </div>
  );
}

/** One sentence about what the request is right now. */
function bannerMessage(request: ApprovalRequestDetail): string {
  switch (request.status) {
    case APPROVAL_STATUS.pending:
      if (request.canApprove) return 'This is waiting for your approval.';
      if (request.canAcknowledge) return 'This is waiting for you to acknowledge it.';

      return request.requireAll
        ? `Awaiting ${
            request.steps.filter(
              (s) =>
                s.status === APPROVAL_STEP_STATUS.active ||
                s.status === APPROVAL_STEP_STATUS.waiting,
            ).length
          } more signature(s).`
        : 'Awaiting any one administrator.';

    case APPROVAL_STATUS.approved:
      return 'Approved. The submitting module has been told and has advanced its record.';

    case APPROVAL_STATUS.rejected:
      return request.outcomeReason
        ? `Rejected — ${request.outcomeReason}`
        : 'Rejected. The record went back to whoever submitted it.';

    case APPROVAL_STATUS.retracted:
      return 'The submitter pulled this back before it was decided.';

    default:
      return 'The record behind this was withdrawn, so the request was closed without a decision.';
  }
}
